import { useState, useEffect, useMemo } from 'react'
import { useLocation } from 'wouter'
import { Card } from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import {
	AlertCircle,
	Clock,
	ChevronLeft,
	ChevronRight,
	Loader2,
	Ticket,
	ExternalLink,
	Phone
} from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { apiFetch } from '@/utils'
import { chargePayment } from '@/utils/charge'
import { useUser } from '@/hooks/use-user'
import { useGetActiveMemberships } from '@/hooks/use-get-active-memberships'
import { getSignedTransactionData } from '@/utils/transactions'
import { useGetActiveEntryPass } from '@/hooks/use-get-active-entry-pass'
import { usePrices } from '@/hooks/use-prices'
import { mutate } from 'swr'
import AvailableEntryPassCard from '@/components/AvailableEntryPassCard'
import { EntryPassTemplate, GuestType } from '@/constants'
import { Button } from '@/components/ui/button'
import type { SelectablePaymentMethod } from '@/types/payment-methods'
import type { ApplicableDiscount } from '@/types/pos'
import ConfirmModal from '@/components/common/ConfirmationModal'
import CheckoutDialog from '@/components/CheckoutDialog'
import type { CheckoutDialogPass } from '@/components/CheckoutDialog'
import useSWR from 'swr'

interface TaxCalculationResult {
	subtotal: number
	fees: Array<{
		type: string
		rate: number
		amount: number
		description: string
	}>
	total: number
}

interface EnhancedPassOption {
	id: number
	name: string
	price: number
	startTime: string
	endTime: string
	dayOfWeek: number
	timeSlot: string
	description: string
	isAvailable: boolean
	guestType: GuestType
	usdPrice: number
	sechPrice: number
	taxCalculation?: TaxCalculationResult
	totalSechPrice?: number
}

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

function getTimeSlotDescription(timeSlot: string): string {
	return timeSlot === 'daytime' ? 'Daytime entry' : 'Evening entry'
}

/**
 * Check if an entry pass is currently within its valid time window.
 * Returns true only if the current time is between the pass start and
 * its validFor expiry. A future pass (start hasn't arrived yet) or an
 * expired pass (validFor is in the past) returns false.
 */
function isEntryPassCurrentlyValid(pass: { validFor?: string; startTime?: string; endTime?: string; dayOfWeek?: number } | null): boolean {
	if (!pass?.validFor) return false
	const now = new Date()
	const validUntil = new Date(pass.validFor)
	// If the pass has already expired, it's not valid
	if (now > validUntil) return false
	// If we have start/end time info, check if we've reached the start window
	if (pass.startTime && pass.dayOfWeek !== undefined) {
		const [startH, startM] = pass.startTime.split(':').map(Number)
		const [endH, endM] = (pass.endTime || '23:59').split(':').map(Number)
		const startMinutes = startH * 60 + startM
		const endMinutes = endH * 60 + endM
		const isOvernight = endMinutes < startMinutes
		const currentDay = now.getDay()
		const currentMinutes = now.getHours() * 60 + now.getMinutes()
		// Same day as the pass
		if (currentDay === pass.dayOfWeek) {
			if (isOvernight) {
				// Overnight pass: valid from start onwards today
				return currentMinutes >= startMinutes
			}
			// Same-day pass: valid within the range
			return currentMinutes >= startMinutes && currentMinutes <= endMinutes
		}
		// Day after: overnight pass still in the morning portion
		const passDay = pass.dayOfWeek
		const dayAfterPass = (passDay + 1) % 7
		if (currentDay === dayAfterPass && isOvernight) {
			return currentMinutes <= endMinutes
		}
		// Not the right day at all — pass is for a future day
		return false
	}
	// No time info — just check validFor
	return true
}

function getCardSpanClass(index: number, total: number): string {
	if (total === 1) return 'col-span-12'
	if (total === 2) return 'col-span-12 sm:col-span-6'
	if (total <= 3) return 'col-span-12 sm:col-span-4'
	// More than 3: rows of 3, remainder gets full-span treatment
	const remainder = total % 3
	const lastRowStart = total - remainder
	if (index < lastRowStart) return 'col-span-12 sm:col-span-4'
	// Last row
	if (remainder === 1) return 'col-span-12'
	if (remainder === 2) return 'col-span-12 sm:col-span-6'
	return 'col-span-12 sm:col-span-4'
}

export default function EntryPassesPage() {
	const [isProcessing, setIsProcessing] = useState(false)
	const [selectedPass, setSelectedPass] = useState<number | null>(null)
	const [selectedGuestType, setSelectedGuestType] = useState<GuestType | null>(null)
	const [validationError, setValidationError] = useState<string | null>(null)
	const [isValidating, setIsValidating] = useState(true)
	const [taxCalculations, setTaxCalculations] = useState<{ [key: string]: TaxCalculationResult }>({})
	const [isCalculatingTaxes, setIsCalculatingTaxes] = useState(false)
	const [hasTaxesLoadedOnce, setHasTaxesLoadedOnce] = useState(false)
	const [, navigate] = useLocation()
	const { toast } = useToast()
	const [currentTime, setCurrentTime] = useState(new Date())

	const { uid, isConnected, walletAddress, loading, connectWallet } = useUser()
	const { convertUsdToToken } = usePrices()
	const {
		activeMemberships,
		isLoading: isMembershipLoading,
		hasActiveMemberships,
		hasAllMembershipsExpired,
		mostRecentActiveMembership
	} = useGetActiveMemberships(walletAddress)
	const {
		activeEntryPass: currentEntryPass,
		activeEntryPasses,
		isEntryPassLoading: isCurrentEntryPassLoading,
		refetch: refetchActiveEntryPass
	} = useGetActiveEntryPass(walletAddress)

	const [showCheckoutDialog, setShowCheckoutDialog] = useState(false)
	const [showBookingConfirm, setShowBookingConfirm] = useState(false)

	// Derive a currently valid pass: prefer the dedicated endpoint, fall back to
	// finding one from the history list so the banner still shows when the main
	// endpoint returns 404 or an unexpected shape.
	const validEntryPass = useMemo(() => {
		if (currentEntryPass && isEntryPassCurrentlyValid(currentEntryPass)) {
			return currentEntryPass
		}
		return activeEntryPasses.find(p => isEntryPassCurrentlyValid(p)) || null
	}, [currentEntryPass, activeEntryPasses])

	// Set of template IDs that already have an active pass
	const activeTemplateIds = new Set(activeEntryPasses.map(p => p.templateId))

	// Auto-set guest type from active membership
	useEffect(() => {
		if (mostRecentActiveMembership?.guestType) {
			setSelectedGuestType(mostRecentActiveMembership.guestType)
		}
	}, [mostRecentActiveMembership])

	// Fetch templates from API
	const { data: templatesResponse, error: templatesError, isLoading: isTemplatesLoading } = useSWR<{ success: boolean; data: EntryPassTemplate[] }>(
		'/v1/entry-passes/templates',
		async () => {
			const response = await apiFetch('/v1/entry-passes/templates', {
				withCredentials: true
			})
			return response.json()
		}
	)

	const templates = templatesResponse?.data || []

	// Filter to active templates for today + previous day's overnight passes
	const availableTemplates = useMemo(() => {
		const today = currentTime.getDay()
		const yesterday = (today + 6) % 7
		return templates.filter(t => {
			if (!t.isActive) return false
			// Include all of today's templates
			if (t.dayOfWeek === today) return true
			// Include yesterday's overnight passes only if still within their end time
			if (t.dayOfWeek === yesterday) {
				const [startHour, startMinute] = t.startTime.split(':').map(Number)
				const [endHour, endMinute] = t.endTime.split(':').map(Number)
				const start = startHour * 60 + startMinute
				const end = endHour * 60 + endMinute
				const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes()
				return end < start && currentMinutes <= end
			}
			return false
		})
	}, [templates, currentTime])

	// Update time every minute
	useEffect(() => {
		const timer = setInterval(() => {
			setCurrentTime(new Date())
		}, 60000)

		return () => clearInterval(timer)
	}, [])

	// Get price for a template based on guest type
	const getPriceForGuestType = (template: EntryPassTemplate, guestType: GuestType): number => {
		switch (guestType) {
			case 'couples':
				return parseFloat(template.priceCouples) || 0
			case 'single_ladies':
				return parseFloat(template.priceSingleLadies) || 0
			case 'single_males':
				return parseFloat(template.priceSingleMales) || 0
			default:
				return parseFloat(template.priceCouples) || 0
		}
	}

	// Enhanced passes with SECH pricing and tax calculations
	const passesWithSechPricing = useMemo(() => {
		if (!selectedGuestType) return []
		return availableTemplates.map(template => {
			const usdPrice = getPriceForGuestType(template, selectedGuestType)
			const sechPrice = convertUsdToToken(usdPrice, 'sech')
			const taxKey = `${template.id}-${selectedGuestType}`
			const taxCalculation = taxCalculations[taxKey]
			const totalSechPrice = taxCalculation ? convertUsdToToken(taxCalculation.total, 'sech') : sechPrice
			return {
				id: template.id,
				name: template.name,
				price: usdPrice,
				startTime: template.startTime,
				endTime: template.endTime,
				dayOfWeek: template.dayOfWeek,
				timeSlot: template.timeSlot,
				description: getTimeSlotDescription(template.timeSlot),
				isAvailable: (() => {
					const [sH, sM] = template.startTime.split(':').map(Number)
					const [eH, eM] = template.endTime.split(':').map(Number)
					const startMin = sH * 60 + sM
					const endMin = eH * 60 + eM
					const nowMin = currentTime.getHours() * 60 + currentTime.getMinutes()
					const isOvernight = endMin < startMin
					// Non-overnight: expired if past end time
					if (!isOvernight && nowMin > endMin) return false
					return true
				})(),
				guestType: selectedGuestType,
				usdPrice,
				sechPrice,
				taxCalculation,
				totalSechPrice
			} as EnhancedPassOption
		})
	}, [availableTemplates, selectedGuestType, convertUsdToToken, taxCalculations, currentTime])

	// Calculate taxes for all passes when guest type changes
	useEffect(() => {
		if (availableTemplates.length === 0 || !selectedGuestType) return

		const calculateTaxes = async () => {
			setIsCalculatingTaxes(true)
			const calculations: { [key: string]: TaxCalculationResult } = {}

			try {
				const taxPromises = availableTemplates.map(async (template) => {
					const price = getPriceForGuestType(template, selectedGuestType)
					const taxKey = `${template.id}-${selectedGuestType}`

					try {
						const response = await apiFetch('/v1/taxes/calculate', {
							method: 'POST',
							headers: {
								'Content-Type': 'application/json',
							},
							body: JSON.stringify({
								amount: price,
								transactionType: 'entry_pass'
							})
						})

						if (!response.ok) {
							throw new Error(`HTTP error! status: ${response.status}`)
						}

						const result = await response.json()
						if (result.success) {
							return { key: taxKey, data: result.data }
						} else {
							throw new Error(result.message || 'Tax calculation failed')
						}
					} catch (error) {
						console.error(`Failed to calculate taxes for template ${template.id}:`, error)
						return {
							key: taxKey,
							data: {
								subtotal: price,
								fees: [],
								total: price
							}
						}
					}
				})

				const results = await Promise.all(taxPromises)

				results.forEach(result => {
					if (result) {
						calculations[result.key] = result.data
					}
				})

			} catch (error) {
				console.error('Error calculating taxes:', error)
				toast({
					title: 'Tax Calculation Error',
					description: 'Unable to calculate taxes. Showing base prices.',
					variant: 'destructive'
				})

				availableTemplates.forEach(template => {
					const price = getPriceForGuestType(template, selectedGuestType)
					const taxKey = `${template.id}-${selectedGuestType}`
					calculations[taxKey] = {
						subtotal: price,
						fees: [],
						total: price
					}
				})
			} finally {
				setTaxCalculations(prev => ({ ...prev, ...calculations }))
				setIsCalculatingTaxes(false)
				setHasTaxesLoadedOnce(true)
			}
		}

		calculateTaxes()
	}, [availableTemplates, selectedGuestType, toast])

	// Enhanced validation for direct access
	useEffect(() => {
		const validateAccess = async () => {
			setIsValidating(true)
			setValidationError(null)
			if (isCurrentEntryPassLoading || isMembershipLoading || loading) return

			try {
				if (!hasActiveMemberships) {
					setValidationError(
						'Please select a membership plan before accessing passes.'
					)
					setTimeout(() => navigate('/membership'), 3000)
					return
				}

				if (hasAllMembershipsExpired) {
					localStorage.removeItem('selectedMembership')
					setValidationError(
						'Your membership selection has expired. Please select a plan again.'
					)
					setTimeout(() => navigate('/membership'), 3000)
					return
				}

				if (walletAddress) {
					const response = await apiFetch(`/v1/waivers/my`, {
						withCredentials: true
					})
					if (!response.ok) {
						throw new Error('Failed to verify waiver status')
					}

					const waiverStatus = await response.json()

					if (!waiverStatus.completed) {
						setValidationError(
							'Please complete both required waivers before accessing passes.'
						)
						setTimeout(() => navigate('/waivers'), 3000)
						return
					}
				}
			} catch (error) {
				console.error('Validation error:', error)
				setValidationError(
					'An error occurred while validating your access. Please try again.'
				)
				setTimeout(() => navigate('/membership'), 3000)
			} finally {
				setIsValidating(false)
			}
		}

		validateAccess()
	}, [
		navigate,
		loading,
		walletAddress,
		hasAllMembershipsExpired,
		isMembershipLoading,
		activeMemberships,
		isCurrentEntryPassLoading
	])

	const handlePurchase = async (passId: number, paymentMethod: SelectablePaymentMethod, discount: ApplicableDiscount | null) => {
		const pass = passesWithSechPricing.find((p) => p.id === passId)
		if (!pass) {
			toast({
				title: 'Pass Unavailable',
				description: 'This pass could not be found. Please try again.',
				variant: 'destructive'
			})
			return
		}

		if (!pass.taxCalculation) {
			toast({
				title: 'Tax Calculation Error',
				description: 'Tax calculation not available. Please try again.',
				variant: 'destructive'
			})
			return
		}

		const selectedPaymentMethod = paymentMethod
		const appliedDiscount = discount

		setIsProcessing(true)
		try {
			let signature: string | undefined
			let forwardRequest: unknown | undefined

			const discountAmt = appliedDiscount ? appliedDiscount.calculatedAmount : 0
			let chargeAmount: number
			if (discountAmt > 0) {
				// Recalculate tax on discounted subtotal (discount first, then tax)
				const discountedSubtotal = pass.taxCalculation.subtotal - discountAmt
				try {
					const taxRes = await apiFetch('/v1/taxes/calculate', {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({
							amount: Math.max(0, discountedSubtotal),
							transactionType: 'entry_pass'
						})
					})
					const taxResult = await taxRes.json()
					chargeAmount = taxResult.success ? taxResult.data.total : discountedSubtotal
				} catch {
					chargeAmount = discountedSubtotal
				}
			} else {
				chargeAmount = pass.taxCalculation.total
			}

			if (selectedPaymentMethod.type === 'TOKEN') {
				// TOKEN flow: connect wallet + get signature
				toast({
					title: 'Processing Payment',
					description: `Processing your ${pass.name} purchase...`
				})

				if (!walletAddress) {
					await connectWallet()
					if (!walletAddress) {
						throw new Error('Please connect your wallet to continue')
					}
				}

				if (isConnected) {
					const sechAmount = convertUsdToToken(chargeAmount, 'sech')
					const {
						signedTx,
						transferRequest: signatureData
					} = await getSignedTransactionData({
						userAddress: walletAddress,
						amount: sechAmount.toString()
					})
					signature = signedTx
					forwardRequest = signatureData
				}
			} else {
				// CARD flow: no wallet signature needed
				toast({
					title: 'Processing Payment',
					description: 'Processing credit card payment...'
				})
			}

			// Process payment through unified charge endpoint
			await chargePayment({
				transactionType: 'entry_pass',
				paymentMethodType: selectedPaymentMethod.type,
				amount: chargeAmount,
				...(selectedPaymentMethod.type === 'CARD' && { paymentMethodId: selectedPaymentMethod.id }),
				userUid: uid,
				walletAddress,
				...(selectedPaymentMethod.type === 'TOKEN' && { signature, forwardRequest }),
				...(appliedDiscount && {
					discountId: appliedDiscount.discountId,
					discountCode: appliedDiscount.promoCode,
					discountAmount: appliedDiscount.calculatedAmount,
				}),
				context: {
					templateId: pass.id,
					entryPassTemplateId: pass.id,
					guestType: selectedGuestType,
				},
			})

			localStorage.removeItem('selectedMembership')
			setSelectedPass(null)
			setShowCheckoutDialog(false)

			await mutate(`/v1/transactions/${uid}`)

			// Refetch active pass with retry — backend may need a moment to create it
			let fetchedPass = await refetchActiveEntryPass()
			if (!fetchedPass) {
				await new Promise(r => setTimeout(r, 1500))
				await refetchActiveEntryPass()
			}

			toast({
				title: 'Success!',
				description: `Your ${pass.name} has been activated.`
			})

		} catch (error) {
			toast({
				title: 'Purchase Failed',
				description:
					error instanceof Error ? error.message : 'Failed to purchase pass',
				variant: 'destructive'
			})
		} finally {
			setIsProcessing(false)
		}
	}

	// Build checkout dialog pass data from selected pass
	const checkoutDialogPass = useMemo((): CheckoutDialogPass | null => {
		if (!selectedPass || !selectedGuestType) return null
		const pass = passesWithSechPricing.find((p) => p.id === selectedPass)
		if (!pass?.taxCalculation) return null
		return {
			id: pass.id,
			name: pass.name,
			guestType: pass.guestType,
			dayOfWeek: pass.dayOfWeek,
			timeSlot: pass.timeSlot,
			taxCalculation: pass.taxCalculation,
			sechPrice: pass.sechPrice,
			totalSechPrice: pass.totalSechPrice,
		}
	}, [selectedPass, selectedGuestType, passesWithSechPricing])

	if (isValidating) {
		return (
			<div className="min-h-screen bg-[#18181B] flex items-center justify-center">
				<Card className="p-6 text-center bg-[#3C3C3C] border-0">
					<Clock className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
					<h2 className="text-lg font-semibold mb-2 text-white">Validating Access</h2>
						<p className="text-sm text-[#BABABB]">
						Please wait while we verify your membership and waivers...
					</p>
				</Card>
			</div>
		)
	}

	if (validationError) {
		return (
			<div className="min-h-auto bg-[#18181B]">
				<div className="px-5 py-4 flex items-center justify-center min-h-[60vh]">
					<Alert variant="destructive" className="max-w-lg">
						<AlertCircle className="h-4 w-4" />
						<AlertDescription className="flex flex-col gap-2">
							<p>{validationError}</p>
							<p className="text-sm">Redirecting you shortly...</p>
						</AlertDescription>
					</Alert>
				</div>
			</div>
		)
	}

	return (
		<div className="min-h-auto bg-[#18181B]">
			<div className="max-w-5xl mx-auto px-5 pb-8">
				<div
					role="banner"
					className="z-50 sticky top-0 bg-[#18181B] pb-[12px] backdrop-blur-[8px] mb-[12px] h-auto flex items-center justify-between text-white py-3"
				>
					<div className="flex items-center gap-2">
						<button
							type="button"
							aria-label="Go back"
							onClick={() => navigate('/')}
							className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
						>
							<ChevronLeft className='h-[22px] w-[22px] text-[#ffffff]' />
						</button>

						<h1 className="text-[20px] leading-[18px] font-bold font-montserrat tracking-wide">
							Entry Passes
						</h1>
					</div>
					<div className="flex items-center text-sm text-[#BABABB]">
						<Clock className="h-4 w-4 mr-1.5" />
						<span>{currentTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</span>
					</div>
				</div>

				{validEntryPass && (
					<button
						onClick={() => navigate('/entry-pass')}
						className="w-full mb-4 flex items-center justify-between bg-green-500/15 border border-green-500/30 rounded-[12px] px-4 py-3"
					>
						<div className="flex items-center gap-3">
							<div className="h-10 w-10 rounded-full bg-green-500/20 flex items-center justify-center">
								<Ticket className="h-5 w-5 text-green-500" />
							</div>
							<div className="text-left">
								<p className="text-white font-semibold text-sm">Active Entry Pass</p>
								<p className="text-green-400 text-xs">Tap to view your ticket</p>
							</div>
						</div>
						<ChevronRight className="h-5 w-5 text-green-400" />
					</button>
				)}

				<div className="text-start mb-6">
					<h1 className="text-[16px] leading-[18px] font-semibold font-figtree text-[#ffffff] mb-2">
						{DAYS_OF_WEEK[currentTime.getDay()]} Entry Passes
					</h1>
					<p className="text-[13px] leading-[15px] font-normal text-[#BABABB]">
						Choose a pass. All prices include applicable taxes.
					</p>
				</div>

				{isTemplatesLoading ? (
					<div className="flex items-center justify-center py-12">
						<Loader2 className="h-8 w-8 animate-spin text-primary" />
						<span className="ml-2 text-[#BABABB]">Loading entry passes...</span>
					</div>
				) : templatesError ? (
					<Alert variant="destructive">
						<AlertCircle className="h-4 w-4" />
						<AlertDescription>
							Failed to load entry passes. Please try again later.
						</AlertDescription>
					</Alert>
				) : availableTemplates.length === 0 ? (
					<Card className="p-6 bg-[#3C3C3C] border-0 text-center">
						<p className="text-[#BABABB]">
							No entry passes available for {DAYS_OF_WEEK[currentTime.getDay()]}. Please check back later.
						</p>
					</Card>
				) : (
					<>
						<div className="grid grid-cols-12 gap-4">
							{passesWithSechPricing.map((pass, index) => (
								<div key={pass.id} className={getCardSpanClass(index, passesWithSechPricing.length)}>
									<AvailableEntryPassCard
										pass={pass}
										isSelected={selectedPass === pass.id}
										isCurrent={activeTemplateIds.has(pass.id)}
										canBuyToday={!isProcessing}
										isLoading={isCalculatingTaxes && !hasTaxesLoadedOnce}
										isProcessing={isProcessing && selectedPass === pass.id}
										onSelect={(clickedPass) => {
											if ((isCalculatingTaxes && !hasTaxesLoadedOnce) || !clickedPass.taxCalculation || isProcessing || clickedPass.price === 0 || activeTemplateIds.has(clickedPass.id) || !clickedPass.isAvailable) {
												return
											}
											setSelectedPass(clickedPass.id)
											setShowCheckoutDialog(true)
										}}
									/>
								</div>
							))}
						</div>
						{isCalculatingTaxes && !hasTaxesLoadedOnce && (
							<p className="text-primary mt-4 flex items-center justify-center gap-2 text-sm">
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
								Calculating taxes...
							</p>
						)}
					</>
				)}
				{/* Book a Room CTA */}
				<div className="mt-8 rounded-[16px] bg-[#2F2F32] border border-white/10 overflow-hidden">
					<div className="px-6 py-5 text-center">
						<h3 className="text-lg font-semibold text-white mb-1">Need to Book a Room?</h3>
						<p className="text-sm text-[#BABABB] mb-4">Reserve your Room at Secrets Hideaways</p>
						<Button
							onClick={() => setShowBookingConfirm(true)}
							className="w-full h-[42px] bg-[#EF4444] hover:bg-[#DC2626] rounded-[21px] shadow-[0px_4px_36px_0px_#DC262699] text-white font-semibold flex items-center justify-center gap-2"
						>
							<ExternalLink className="h-4 w-4" />
							Book Now
						</Button>
						<div className="mt-4 border-t border-white/10 pt-4">
							<p className="text-sm text-[#BABABB] mb-2">Or Call Toll Free</p>
							<a
								href="tel:8445636969"
								className="inline-flex items-center gap-2 text-primary font-semibold text-lg hover:underline"
							>
								<Phone className="h-4 w-4" />
								844-563-6969
							</a>
						</div>
					</div>
				</div>
			</div>
			<CheckoutDialog
				open={showCheckoutDialog}
				onClose={() => setShowCheckoutDialog(false)}
				pass={checkoutDialogPass}
				isProcessing={isProcessing}
				onConfirmPayment={(method, discount) => {
					if (selectedPass) {
						handlePurchase(selectedPass, method, discount)
					}
				}}
			/>
				<ConfirmModal
				open={showBookingConfirm}
				onClose={() => setShowBookingConfirm(false)}
				onConfirm={() => {
					setShowBookingConfirm(false)
					window.open('https://www.secretsfl.com/book-now-iframe', '_blank')
				}}
				title="You're leaving the app"
				description="You will be redirected to the Secrets Hideaways website to complete your room booking. The booking site's terms and policies will apply."
				confirmText="Continue to Booking"
				cancelText="Stay in SECH"
			/>
		</div>
	)
}
